import { useEffect, useRef, useState } from "react";
import { Stack, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import {
  CheckboxFormikField,
  Divider,
  List,
  ListItem,
  NumberFieldFormikField,
  Radio,
  RadioGroupFormikField,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { Trans, useTranslation } from "react-i18next";
import {
  EDUCATION_LEVEL_OPTIONS,
  EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG,
  ExperienceVerificationTemplateFormType,
  GAP_THRESHOLD_UNIT_OPTIONS,
  GAP_VERIFICATION_OPTIONS,
} from "./utils";
import { ReferenceRuleOptionSelect } from "./ReferenceRuleOptionSelect";
import { FormikProps } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { InstantEmploymentVerificationToggle } from "../../components/verification-templates/InstantEmploymentVerificationToggle";

export type ExperienceVerificationTemplateFormProps = {
  values: ExperienceVerificationTemplateFormType;
  setValues: FormikProps<ExperienceVerificationTemplateFormType>["setValues"];
  isDisabled?: boolean;
};

export const ExperienceVerificationTemplateForm = ({
  values,
  setValues,
  isDisabled,
}: ExperienceVerificationTemplateFormProps) => {
  const { t } = useTranslation();
  const {
    releaseGapDocumentation,
    enableSettingEducationLevelInExperienceVerificationTemplates,
  } = useFlags();

  const prevIncludeGapRef = useRef(values.include_gap_detection);
  const [bpssGapOffWarning, setBpssGapOffWarning] = useState(false);

  useEffect(() => {
    if (values.include_gap_detection) {
      setBpssGapOffWarning(false);
      prevIncludeGapRef.current = true;
      return;
    }
    const prev = prevIncludeGapRef.current;
    prevIncludeGapRef.current = values.include_gap_detection;
    if (prev === true && values.reference_rule_options === "BPSS") {
      setBpssGapOffWarning(true);
      setValues((p) => ({
        ...p,
        reference_rule_options: undefined,
        gap_verification_type: "STATEMENT",
      }));
    }
  }, [values.include_gap_detection, values.reference_rule_options, setValues]);

  const handleDigitalVerificationToggle = (selected: boolean) => {
    setValues((prev) => ({
      ...prev,
      digital_verification: selected,
    }));
  };

  const handleGapVerificationChange = (value: string) => {
    if (value === "STATEMENT" || value === "DOCUMENTATION") {
      setValues((prev) => ({
        ...prev,
        gap_verification_type: value,
        reference_rule_options: undefined,
      }));
    }

    if (value === "REFERENCE") {
      setValues((prev) => ({
        ...prev,
        gap_verification_type: "REFERENCE",
        reference_rule_options: "DEFAULT",
      }));
    }
  };

  const handleUnitChange = (unit: string) => {
    if (unit === "MONTHS") {
      setValues((prev) => ({
        ...prev,
        gap_threshold_unit: "MONTHS",
        minimum_gap_period_months:
          prev.minimum_gap_period_months ??
          EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_MINIMUM_GAP_PERIOD_MONTHS,
        minimum_gap_period_days: undefined,
      }));
    } else {
      setValues((prev) => ({
        ...prev,
        gap_threshold_unit: "DAYS",
        minimum_gap_period_months: undefined,
        minimum_gap_period_days: undefined,
      }));
    }
  };

  const isMonths = values.gap_threshold_unit !== "DAYS";

  return (
    <Stack gap="space-6">
      <TextInputFormikField
        name="name"
        isRequired
        label={t("Template name")}
        testID="template-name"
        description={t(
          "Used internally to identify the template. The applicant will not see this."
        )}
      />
      <Divider />
      <Stack gap="space-5">
        <ExperienceVerificationTemplateFormField
          title={t("Experience lookback period")}
          description={t(
            "Choose how many years of experience are reviewed. Only employment and education within this period are considered when assessing coverage and gaps."
          )}
        />
        <NumberFieldFormikField
          label={t("Look back period years")}
          name="lookback_period_years"
          minValue={
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_LOOKBACK_PERIOD_MONTHS /
            12
          }
          maxValue={
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_LOOKBACK_PERIOD_MONTHS /
            12
          }
          defaultValue={
            EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.DEFAULT_LOOKBACK_PERIOD_MONTHS /
            12
          }
          testID="minimum-duration"
          isRequired
          description={t(
            "Note: Certain security standards (such as BPSS) require gaps longer than 3 months to be queried and supported with additional verification."
          )}
          isDisabled={isDisabled}
        />
      </Stack>
      <Divider />
      <Stack gap="space-5">
        <ExperienceVerificationTemplateFormField
          title={t("Gap verification")}
          description={t(
            "Control whether gaps are identified and when they require verification."
          )}
        />
        <Stack gap="space-1">
          <CheckboxFormikField
            name="include_gap_detection"
            testID="include-gap-detection"
            isDisabled={isDisabled}
          >
            {t("Include gap detection and verification")}
          </CheckboxFormikField>
          <Text
            style="form/body-secondary"
            color="text-weak"
            className={css({ paddingLeft: "var(--space-8)" })}
          >
            {t("Ask applicants to explain gaps in their history")}
          </Text>
        </Stack>
      </Stack>
      {bpssGapOffWarning && !values.include_gap_detection && (
        <SectionMessage type="warning" icon="warning">
          <Text style="global/body-secondary">
            {t(
              "BPSS checks typically require gap verification. Disabling this may result in incomplete screening."
            )}
          </Text>
        </SectionMessage>
      )}
      {values.include_gap_detection && (
        <>
          <Divider />
          <Stack gap="space-5">
            <ExperienceVerificationTemplateFormField
              title={t("Gap threshold")}
              description={t(
                "Set the minimum length of a gap before additional information is required. Gaps shorter than this threshold are not recorded."
              )}
            />
            <Stack gap="space-1">
              <Text style="input/label">{t("Minimum gap length")}</Text>
              <Stack orientation="horizontal" gap="space-3" align="start">
                <div className={narrowSelectStyle}>
                  <SelectFormikField
                    label=""
                    name="gap_threshold_unit"
                    isRequired
                    testID="gap-threshold-unit"
                    isDisabled={isDisabled}
                    onSelectionChange={(value) =>
                      handleUnitChange(String(value))
                    }
                  >
                    {GAP_THRESHOLD_UNIT_OPTIONS().map((option) => (
                      <SelectItem
                        key={option.value}
                        id={option.value}
                        textValue={option.label}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectFormikField>
                </div>
                <TextInputFormikField
                  name={
                    isMonths
                      ? "minimum_gap_period_months"
                      : "minimum_gap_period_days"
                  }
                  label=""
                  isRequired
                  testID="minimum-gap-period-value"
                  isDisabled={isDisabled}
                  placeholder={isMonths ? "6" : "1"}
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="[0-9]*"
                />
              </Stack>
              <Text style="form/body-secondary" color="text-weak">
                {t(
                  "For example, if this is set to 6 months, applicants will only be asked to explain gaps lasting 6 months or more."
                )}
              </Text>
            </Stack>
            <Stack gap="space-1">
              <CheckboxFormikField
                name="include_cumulative_gap_detection"
                testID="include-cumulative-gap-detection"
                isDisabled={isDisabled}
              >
                {t("Include cumulative gap detection")}
              </CheckboxFormikField>
              <Text style="form/body-secondary" color="text-weak">
                {t("Include cumulative gap detection description", {
                  defaultValue:
                    "Considers total gap duration across the lookback period. Verification is triggered if either a single gap or the total gap duration meets the threshold.",
                })}
              </Text>
            </Stack>
          </Stack>
          <Divider />
          <Stack gap="space-5">
            <ExperienceVerificationTemplateFormField
              title={t("Gap detail requirements")}
              description={t(
                "For gaps that meet or exceed your threshold, choose the level of detail required."
              )}
            />
            <RadioGroupFormikField
              isCard
              testID="gap-verification-type"
              name="gap_verification_type"
              orientation="vertical"
              isRequired
              defaultValue="STATEMENT"
              onChange={(value) => {
                handleGapVerificationChange(value);
              }}
              isDisabled={isDisabled}
            >
              {GAP_VERIFICATION_OPTIONS()
                .filter(
                  (option) =>
                    releaseGapDocumentation || option.key !== "documentation"
                )
                .map((option) => (
                  <Radio
                    key={option.key}
                    testID={`gap-verification-${option.key}-type`}
                    value={option.value}
                    description={option.description}
                    options={
                      option.key === "reference" &&
                      values.gap_verification_type === "REFERENCE" ? (
                        <ReferenceRuleOptionSelect isDisabled={isDisabled} />
                      ) : undefined
                    }
                  >
                    {option.label}
                  </Radio>
                ))}
            </RadioGroupFormikField>
          </Stack>
          {values.reference_rule_options === "BPSS" && (
            <SectionMessage
              type="info"
              sectionTitle={t("BPSS Rules")}
              icon="info"
            >
              <List type="unordered">
                <ListItem>
                  <Text style="global/body-secondary" color="text-weak">
                    <Trans>BPSS requires a 3-year lookback period</Trans>
                  </Text>
                </ListItem>
                <ListItem>
                  <Text style="global/body-secondary" color="text-weak">
                    <Trans>
                      BPSS requires gaps longer than 3 months to be queried
                    </Trans>
                  </Text>
                </ListItem>
              </List>
            </SectionMessage>
          )}
        </>
      )}
      {enableSettingEducationLevelInExperienceVerificationTemplates && (
        <>
          <Divider />
          <Stack gap="space-5">
            <ExperienceVerificationTemplateFormField
              title={t("Education verification requirements")}
              description={t(
                "Choose what education should be collected and verified in addition to the experience lookback period."
              )}
            />
            <SelectFormikField
              label={t("Education to verify")}
              name="verification_type"
              testID="education-level"
              isDisabled={isDisabled}
            >
              {EDUCATION_LEVEL_OPTIONS().map((option) => (
                <SelectItem
                  key={option.value}
                  id={option.value}
                  textValue={option.label}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectFormikField>
          </Stack>
        </>
      )}
      <InstantEmploymentVerificationToggle
        isReadOnly={isDisabled}
        digital_verification={values.digital_verification ?? false}
        onChange={handleDigitalVerificationToggle}
      />
    </Stack>
  );
};

const narrowSelectStyle = css({ width: "140px", flexShrink: 0 });

export const ExperienceVerificationTemplateFormField = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <Stack gap="space-1">
      <Text style="global/heading-medium">{title}</Text>
      <Text style="form/body-secondary" color="text-weak">
        {description}
      </Text>
    </Stack>
  );
};
